import { dayjs } from "@/core/utils/date";
import { DEFAULT_CURRENCY, formatMoney } from "@/profitability/currency";
import type {
  GoalTolerance,
  TemplateSetting,
  TemplateSettingType,
  TemplateSettingValue,
} from "./types";

type ScalarType = Exclude<TemplateSettingType, "map">;

export type Parsed =
  | { ok: true; value: number | string | null }
  | { ok: false; error: string };

const ISO = "YYYY-MM-DD";

/** Up to four decimals, without trailing zeros: 0.2 → "20" as a percent. */
const trimmed = (value: number) => String(Number.parseFloat(value.toFixed(4)));

/** A stored value as its input holds it: a percent in percent (0.2 → "20"). */
export const toInput = (type: ScalarType, value: TemplateSettingValue) => {
  if (value === null || typeof value === "object") return "";
  if (type === "percent" && typeof value === "number")
    return trimmed(value * 100);
  return String(value);
};

/**
 * What the owner typed, as the API stores it: a percent as a share of one
 * ("20" → 0.2), money and numbers without their commas or sign. Empty is
 * null, which clears the value back to its default.
 */
export const fromInput = (type: ScalarType, text: string): Parsed => {
  const raw = text.trim();
  if (!raw) return { ok: true, value: null };
  if (type === "text") return { ok: true, value: raw };
  if (type === "date") {
    const day = dayjs(raw, ISO, true);
    return day.isValid()
      ? { ok: true, value: day.format(ISO) }
      : { ok: false, error: "Enter a date" };
  }
  const number = Number(raw.replace(/[,%$\s]/g, ""));
  if (!Number.isFinite(number)) return { ok: false, error: "Enter a number" };
  if (type === "percent") {
    if (number < 0 || number > 100)
      return { ok: false, error: "Enter a percent from 0 to 100" };
    return { ok: true, value: Number((number / 100).toFixed(6)) };
  }
  return { ok: true, value: number };
};

/** One value as the page writes it: "20%", "$1,250.00", "Jan 1, 2026". */
export const formatScalar = (
  type: ScalarType,
  value: TemplateSettingValue,
  currency: string = DEFAULT_CURRENCY,
) => {
  if (value === null || typeof value === "object") return "Not set";
  if (typeof value === "number") {
    if (type === "percent") return `${trimmed(value * 100)}%`;
    if (type === "money")
      return formatMoney(value, currency, { maximumFractionDigits: 2 });
    return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (type === "date") return dayjs(value, ISO).format("MMM D, YYYY");
  return value;
};

/** A setting's value, or its default, as the page writes it; a map as "HVAC 48%, Plumbing 45%". */
export const formatSetting = (
  setting: Pick<TemplateSetting, "type" | "value_type">,
  value: TemplateSettingValue,
  currency: string = DEFAULT_CURRENCY,
) => {
  if (setting.type !== "map")
    return formatScalar(setting.type, value, currency);
  if (!value || typeof value !== "object") return "Not set";
  const entries = Object.entries(value);
  if (!entries.length) return "None";
  return entries
    .map(
      ([name, entry]) =>
        `${name} ${formatScalar(setting.value_type ?? "number", entry, currency)}`,
    )
    .join(", ");
};

/** The value in effect: the template's own, else the script's default. */
export const effectiveValue = (setting: TemplateSetting) =>
  setting.value ?? setting.default;

export interface MapRow {
  id: string;
  name: string;
  value: string;
}

let rowCounter = 0;
export const newRowId = () => `row-${(rowCounter += 1)}`;

/** A map's entries as editable rows, each value as its input holds it. */
export const toMapRows = (
  valueType: ScalarType,
  value: TemplateSettingValue,
): MapRow[] =>
  value && typeof value === "object"
    ? Object.entries(value).map(([name, entry]) => ({
        id: newRowId(),
        name,
        value: toInput(valueType, entry),
      }))
    : [];

export interface MapIssue {
  rowId: string;
  column: "name" | "value";
}

/** The rows as the API stores the map; the first problem when they do not fit. */
export const fromMapRows = (
  valueType: ScalarType,
  rows: MapRow[],
):
  | { ok: true; value: Record<string, number> }
  | ({ ok: false; error: string } & MapIssue) => {
  const entries = new Map<string, number>();
  for (const row of rows) {
    const name = row.name.trim();
    if (!name && !row.value.trim()) continue;
    if (!name)
      return {
        ok: false,
        error: "Enter a name for this row",
        rowId: row.id,
        column: "name",
      };
    if (entries.has(name))
      return {
        ok: false,
        error: `${name} is listed twice. Use a different name.`,
        rowId: row.id,
        column: "name",
      };
    const parsed = fromInput(valueType, row.value);
    if (!parsed.ok)
      return {
        ok: false,
        error: `${name}: ${parsed.error}`,
        rowId: row.id,
        column: "value",
      };
    if (typeof parsed.value !== "number")
      return {
        ok: false,
        error: `${name}: enter a value`,
        rowId: row.id,
        column: "value",
      };
    entries.set(name, parsed.value);
  }
  return { ok: true, value: Object.fromEntries(entries) };
};

/** "within $0.01", "within $1 or 0.5%": how close counts as matching. */
export const toleranceLabel = (
  tolerance: GoalTolerance,
  currency: string = DEFAULT_CURRENCY,
) => {
  const amount = formatMoney(tolerance.amount, currency, {
    maximumFractionDigits: 2,
  });
  return tolerance.percent
    ? `within ${amount} or ${trimmed(tolerance.percent * 100)}%`
    : `within ${amount}`;
};
